import { useEffect, useState } from "react";

/**
 * Banner que invita a instalar DMU 614 como PWA.
 *  - Chrome/Edge Android: dispara el prompt nativo `beforeinstallprompt`.
 *  - Safari iOS: no soporta `beforeinstallprompt`, mostramos instrucciones manuales
 *    ("Compartir → Agregar a inicio").
 *  - Si la app ya está instalada (`display-mode: standalone`) o el usuario
 *    descartó el banner antes, no se muestra.
 */
export default function PwaInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [esIOS, setEsIOS] = useState(false);

  useEffect(() => {
    // Ya instalada → ocultar.
    const yaInstalada =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (yaInstalada) return;
    // ¿descartado antes? (no molestar 7 días)
    const dismissAt = Number(localStorage.getItem("pwa_dismiss_at") || 0);
    if (dismissAt && Date.now() - dismissAt < 7 * 24 * 60 * 60 * 1000) return;

    // iOS: detectar Safari para mostrar instrucciones manuales.
    const ua = navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua) && !/(crios|fxios)/.test(ua);
    if (ios) {
      setEsIOS(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible) return null;
  if (window.location.pathname === "/validar-pipas") return null;

  const ahoraNo = () => {
    localStorage.setItem("pwa_dismiss_at", String(Date.now()));
    setVisible(false);
  };

  const instalar = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        setVisible(false);
      } else {
        ahoraNo();
      }
    } catch {
      ahoraNo();
    } finally {
      setDeferred(null);
    }
  };

  return (
    <div
      data-testid="pwa-install-banner"
      style={{
        position: "fixed", left: 12, right: 12, bottom: 12, zIndex: 50,
        background: "#1D4ED8", color: "white", borderRadius: 12,
        boxShadow: "0 8px 20px rgba(15,23,42,0.30)", padding: 12,
        display: "flex", alignItems: "center", gap: 10,
      }}
    >
      <img src="/icon-192.png" alt="" width={40} height={40}
           style={{ borderRadius: 8, background: "#1A1A18", padding: 2 }}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 14 }}>📱 Instala DMU 614 en tu celular</div>
        {esIOS ? (
          <div style={{ fontSize: 12, opacity: 0.9 }}>
            Toca <i className="fa-solid fa-arrow-up-from-bracket"/> Compartir → <strong>Agregar a Inicio</strong>
          </div>
        ) : (
          <div style={{ fontSize: 12, opacity: 0.9 }}>Acceso rápido sin escribir la dirección.</div>
        )}
      </div>
      {!esIOS && (
        <button
          onClick={instalar}
          data-testid="pwa-install-btn"
          style={{ background: "white", color: "#1D4ED8", border: 0, borderRadius: 8, padding: "8px 12px", fontWeight: 800, fontSize: 13, cursor: "pointer" }}
        >
          Instalar
        </button>
      )}
      <button
        onClick={ahoraNo}
        data-testid="pwa-install-dismiss"
        aria-label="Cerrar"
        style={{ background: "transparent", color: "white", border: 0, padding: "4px 8px", fontSize: 18, cursor: "pointer", opacity: 0.85 }}
      >
        ×
      </button>
    </div>
  );
}
